import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { MonthlyStats } from '@/lib/reports';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MonthlyLineChartProps {
  data: MonthlyStats[];
}

export function MonthlyLineChart({ data }: MonthlyLineChartProps) {
  const chartData = {
    labels: data.map((d) => d.month),
    datasets: [
      {
        label: '稼働時間（時間）',
        data: data.map((d) => d.totalHours),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: 'rgb(37, 99, 235)',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          font: {
            size: 14,
            family: "'Noto Sans JP', sans-serif",
          },
          padding: 20,
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          family: "'Noto Sans JP', sans-serif",
        },
        bodyFont: {
          size: 13,
          family: "'Noto Sans JP', sans-serif",
        },
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            const index = context.dataIndex;
            const minutes = data[index].totalMinutes;
            return `${value}時間 (${minutes}分)`;
          },
          afterLabel: (context: any) => {
            const index = context.dataIndex;
            const workDays = data[index].workDays;
            return `稼働日数: ${workDays}日`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            size: 12,
            family: "'Noto Sans JP', sans-serif",
          },
          callback: (value: any) => `${value}h`,
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        ticks: {
          font: {
            size: 12,
            family: "'Noto Sans JP', sans-serif",
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">月次稼働時間推移</h2>
      </div>
      <div className="h-80">
        {data.length > 0 ? (
          <Line data={chartData} options={options} />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            データがありません
          </div>
        )}
      </div>
    </div>
  );
}
